<template>
  <div class="card-body">
    <div class="row row-spacing">
      <div class="col-md-12">
        <div class="nav-tabs-wrapper w-100">
          <ul class="nav nav-tabs responsive-tabs" role="tablist">
            <li class="nav-item" v-for="tab in tabs" :key="tab.key">
              <a
                href="#"
                class="nav-link"
                :class="{ active: activeTab === tab.key }"
                @click.prevent="activeTab = tab.key"
              >
                <i :class="tab.icon"></i>
                <span class="ml-2">{{ tab.title }}</span>
              </a>
            </li>
          </ul>

          <div class="tab-content">
            <div class="tab-pane pb-3" :class="{ 'show active': activeTab === 'contacts' }">
              <div class="card table-responsive mt-4">
                <div class="card-body px-0 pt-3 pb-3">
                  <div class="row align-items-end no-gutters">
                    <div class="col-md-8 pr-md-3">
                      <label>Search</label>
                      <div class="input-group">
                        <input
                          type="search"
                          class="form-control"
                          placeholder="Search by name, job title, email address, or number"
                          v-model="searchTerm"
                        />
                        <div class="input-group-append">
                          <span class="input-group-text"><i class="fa fa-search"></i></span>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 text-right">
                      <button class="btn btn-primary" @click="openCreateModal()">
                        Add Contact
                      </button>
                    </div>
                  </div>
                </div>
                <table class="table table-bordered table-hover">
                  <thead>
                    <tr>
                      <th>First Names</th>
                      <th>Last Name</th>
                      <th>Job Title</th>
                      <th>Email Address</th>
                      <th>Phone Number</th>
                      <th>Cell Number</th>
                      <th>Assigned Role(s)</th>
                      <th style="width: 8rem"></th>
                    </tr>
                  </thead>
                  <tbody v-if="filteredContacts.length">
                    <tr v-for="contact in filteredContacts" :key="contact.supplierContactNumber">
                      <td>{{ contact.firstNames }}</td>
                      <td>{{ contact.lastName }}</td>
                      <td>{{ contact.jobTitle }}</td>
                      <td>{{ contact.emailAddress }}</td>
                      <td>{{ contact.contactPhoneNumber }}</td>
                      <td>{{ contact.contactCellNumber }}</td>
                      <td>{{ getAssignedRoleTitles(contact.supplierContactNumber) }}</td>
                      <td class="text-center">
                        <button
                          class="btn btn-sm btn-info m-1"
                          type="button"
                          v-tooltip="'Edit Contact'"
                          @click="openEditModal(contact)"
                        >
                          <em class="fa fa-edit fa-fw"></em>
                        </button>
                        <button
                          class="btn btn-sm btn-primary"
                          type="button"
                          v-tooltip="'Delete Contact'"
                          v-allowed:view="['STAFF']"
                          @click="deleteContact(contact)"
                        >
                          <em class="fa fa-trash fa-fw"></em>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="8">No Contacts available</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="tab-pane pb-3" :class="{ 'show active': activeTab === 'assignments' }">
              <div class="card card-default border-top-0">
                <div class="card-body">
                  <div class="department-escalations-wrapper">
                    <h4>Supplier Role Assignments</h4>
                    <hr />
                    <div class="form-wrapper">
                      <div class="form-row">
                        <div class="form-group col-md-6">
                          <label>Accounts:</label>
                          <v-select
                            v-model="supplier.accountsNumber"
                            :options="contacts"
                            :get-option-label="getContactLabel"
                            valueProp="supplierContactNumber"
                            placeholder="Select an Accounts contact"
                            :clearable="true"
                            :key="`accounts-${contactsKey}`"
                          >
                          </v-select>
                        </div>
                        <div class="form-group col-md-6">
                          <label>Account Manager:</label>
                          <v-select
                            v-model="supplier.accountsManagerNumber"
                            :options="contacts"
                            :get-option-label="getContactLabel"
                            valueProp="supplierContactNumber"
                            placeholder="Select an Account Manager"
                            :clearable="true"
                            :key="`account-manager-${contactsKey}`"
                          >
                          </v-select>
                        </div>
                      </div>
                      <div class="form-row">
                        <div class="form-group col-md-6">
                          <label>Service Delivery Manager:</label>
                          <v-select
                            v-model="supplier.serviceDeliveryManagerNumber"
                            :options="contacts"
                            :get-option-label="getContactLabel"
                            valueProp="supplierContactNumber"
                            placeholder="Select a Service Delivery Manager"
                            :clearable="true"
                            :key="`service-delivery-${contactsKey}`"
                          >
                          </v-select>
                        </div>
                        <div class="form-group col-md-6">
                          <label>B-BBEE Admin:</label>
                          <v-select
                            v-model="supplier.bbbeeAdminNumber"
                            :options="contacts"
                            :get-option-label="getContactLabel"
                            valueProp="supplierContactNumber"
                            placeholder="Select a B-BBEE Admin contact"
                            :clearable="true"
                            :key="`bbbee-admin-${contactsKey}`"
                          >
                          </v-select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="save-button-wrapper">
                    <button
                      class="btn btn-info m-0"
                      :disabled="assignmentSaving"
                      @click="saveAssignments()"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <modal
        :title="modalTitle"
        ref="contactModal"
        size="modal-lg"
        :disable-save="modalSaving"
        @save="saveContact()"
        @dismiss="resetModalState()"
        @close="resetModalState()"
      >
        <div class="row">
          <div class="form-group col-md-6">
            <label>First Names</label>
            <input
              v-model="modalContact.firstNames"
              type="text"
              class="form-control"
            />
          </div>
          <div class="form-group col-md-6">
            <label>Last Name</label>
            <input
              v-model="modalContact.lastName"
              type="text"
              class="form-control"
            />
          </div>
        </div>

        <div class="row">
          <div class="form-group col-md-6">
            <label>Job Title</label>
            <input
              v-model="modalContact.jobTitle"
              type="text"
              class="form-control"
            />
          </div>
          <div class="form-group col-md-6">
            <label>Email Address</label>
            <input
              v-model="modalContact.emailAddress"
              type="email"
              class="form-control"
              :class="{ 'has-error': validationErrors.emailAddress }"
            />
            <small class="has-error mt-1" v-if="validationErrors.emailAddress">
              {{ validationErrors.emailAddress }}
            </small>
          </div>
        </div>

        <div class="row">
          <div class="form-group col-md-6">
            <label>Phone Number</label>
            <input
              v-model="modalContact.contactPhoneNumber"
              type="text"
              class="form-control"
              :class="{ 'has-error': validationErrors.contactPhoneNumber }"
            />
            <small class="has-error mt-1" v-if="validationErrors.contactPhoneNumber">
              {{ validationErrors.contactPhoneNumber }}
            </small>
          </div>
          <div class="form-group col-md-6">
            <label>Cell Number</label>
            <input
              v-model="modalContact.contactCellNumber"
              type="text"
              class="form-control"
              :class="{ 'has-error': validationErrors.contactCellNumber }"
            />
            <small class="has-error mt-1" v-if="validationErrors.contactCellNumber">
              {{ validationErrors.contactCellNumber }}
            </small>
          </div>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import Modal from "@/components/GeneralComponents/Modal";

export default {
  components: {
    Modal,
  },
  data: function() {
    return {
      activeTab: "contacts",
      supplierNumber: 0,
      supplier: {},
      contacts: [],
      searchTerm: "",
      contactsKey: 0,
      emptyContact: {},
      modalContact: {},
      validationErrors: {},
      modalSaving: false,
      assignmentSaving: false,
      tabs: [
        {
          key: "contacts",
          title: "Contacts",
          icon: "fa fa-address-book",
        },
        {
          key: "assignments",
          title: "Role Assignments",
          icon: "fa fa-level-up",
        },
      ],
      roleConfigs: [
        {
          key: "accounts",
          title: "Accounts",
          supplierField: "accountsNumber",
        },
        {
          key: "accountManager",
          title: "Account Manager",
          supplierField: "accountsManagerNumber",
        },
        {
          key: "serviceDeliveryManager",
          title: "Service Delivery Manager",
          supplierField: "serviceDeliveryManagerNumber",
        },
        {
          key: "bbbeeAdmin",
          title: "B-BBEE Admin",
          supplierField: "bbbeeAdminNumber",
        },
      ],
    };
  },
  created() {
    this.$root.$emit("SET_PAGE_TITLE", this.$route.meta.title);
  },
  mounted: function() {
    this.supplierNumber = parseInt(this.$route.params.supplierNumber);
    this.loadPage();
  },
  computed: {
    modalTitle() {
      return this.modalContact.supplierContactNumber
        ? "Edit Supplier Contact"
        : "Add Supplier Contact";
    },
    filteredContacts() {
      if (!this.searchTerm || this.searchTerm.trim() === "") {
        return this.contacts;
      }

      const searchValue = this.searchTerm.trim().toLowerCase();

      return this.contacts.filter((contact) => {
        const assignedRoles = this.getAssignedRoleTitles(contact.supplierContactNumber).toLowerCase();

        return (
          String(contact.supplierContactNumber).includes(searchValue) ||
          (contact.firstNames && contact.firstNames.toLowerCase().includes(searchValue)) ||
          (contact.lastName && contact.lastName.toLowerCase().includes(searchValue)) ||
          (contact.jobTitle && contact.jobTitle.toLowerCase().includes(searchValue)) ||
          (contact.emailAddress && contact.emailAddress.toLowerCase().includes(searchValue)) ||
          (contact.contactPhoneNumber && contact.contactPhoneNumber.toLowerCase().includes(searchValue)) ||
          (contact.contactCellNumber && contact.contactCellNumber.toLowerCase().includes(searchValue)) ||
          assignedRoles.includes(searchValue)
        );
      });
    },
  },
  methods: {
    loadPage() {
      return Promise.all([
        this.getEmptyContact(),
        this.getSupplier(),
        this.getContacts(),
      ]);
    },
    getSupplier() {
      return this.$http
        .get(this.$config.aimsAPIv3 + "suppliers/" + this.supplierNumber)
        .then(
          (response) => {
            this.supplier = response.data;
            this.$root.$emit(
              "SET_PAGE_TITLE",
              this.$route.meta.title + " - " + this.supplier.name
            );
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Supplier", error);
          }
        );
    },
    getContacts() {
      return this.$http
        .get(this.$config.aimsAPIv3 + "suppliers/" + this.supplierNumber + "/contacts")
        .then(
          (response) => {
            this.contacts = response.data;
            this.contactsKey += 1;
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Contacts", error);
          }
        );
    },
    getEmptyContact() {
      return this.$http.get(this.$config.aimsAPIv3 + "suppliers/empty/contact").then(
        (response) => {
          this.emptyContact = response.data;
        },
        (error) => {
          console.error(error);
          this.showError("Error Fetching Empty Contact", error);
        }
      );
    },
    buildEmptyContact() {
      return {
        class:
          this.emptyContact.class ||
          "za.co.adept.aims.product.supplier.SupplierContact",
        supplierContactNumber: this.emptyContact.supplierContactNumber || 0,
        supplierNumber: this.supplierNumber || this.emptyContact.supplierNumber || 0,
        firstNames: this.emptyContact.firstNames || "",
        lastName: this.emptyContact.lastName || "",
        jobTitle: this.emptyContact.jobTitle || "",
        emailAddress: this.emptyContact.emailAddress || "",
        contactPhoneNumber: this.emptyContact.contactPhoneNumber || "",
        contactCellNumber: this.emptyContact.contactCellNumber || "",
      };
    },
    hasContactValues(contact) {
      const fields = [
        contact.firstNames,
        contact.lastName,
        contact.jobTitle,
        contact.emailAddress,
        contact.contactPhoneNumber,
        contact.contactCellNumber,
      ];

      for (let i = 0; i < fields.length; i++) {
        if (fields[i] && fields[i].toString().trim() !== "") {
          return true;
        }
      }

      return false;
    },
    getRoleConfig(roleKey) {
      for (let i = 0; i < this.roleConfigs.length; i++) {
        if (this.roleConfigs[i].key === roleKey) {
          return this.roleConfigs[i];
        }
      }

      return null;
    },
    getAssignedRoleTitles(supplierContactNumber) {
      const titles = [];

      for (let i = 0; i < this.roleConfigs.length; i++) {
        const role = this.roleConfigs[i];
        if (this.supplier[role.supplierField] === supplierContactNumber) {
          titles.push(role.title);
        }
      }

      return titles.length ? titles.join(", ") : "-";
    },
    getContactLabel(contact) {
      if (!contact) {
        return "";
      }

      const name = [contact.firstNames, contact.lastName]
        .filter((part) => !!part && part.trim() !== "")
        .join(" ")
        .trim();

      if (name) {
        return `${name}${contact.emailAddress ? " - " + contact.emailAddress : ""}`;
      }

      return contact.emailAddress || `Contact #${contact.supplierContactNumber}`;
    },
    openCreateModal() {
      this.validationErrors = {};
      this.modalContact = this.buildEmptyContact();
      this.$refs.contactModal.open();
    },
    openEditModal(contact) {
      this.validationErrors = {};
      this.modalContact = Object.assign(this.buildEmptyContact(), contact);
      this.$refs.contactModal.open();
    },
    resetModalState() {
      this.modalContact = {};
      this.validationErrors = {};
      this.modalSaving = false;
    },
    validateEmailAddress(emailAddress) {
      if (!emailAddress || emailAddress.trim() === "") {
        return "";
      }

      const normalizedEmail = emailAddress.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(normalizedEmail)) {
        return "Please capture a valid email address.";
      }

      return "";
    },
    validatePhoneNumber(value, label) {
      if (!value || value.trim() === "") {
        return "";
      }

      const normalizedValue = value.trim();
      const phoneRegex = /^[0-9]+$/;
      const digitsOnlyLength = normalizedValue.length;

      if (!phoneRegex.test(normalizedValue)) {
        return `${label} must contain digits only.`;
      }

      if (digitsOnlyLength < 9) {
        return `${label} must contain at least 9 digits.`;
      }

      if (digitsOnlyLength > 22) {
        return `${label} must contain no more than 22 digits.`;
      }

      return "";
    },
    validateContact(contact) {
      const errors = {};

      const emailAddressError = this.validateEmailAddress(contact.emailAddress);
      if (emailAddressError) {
        errors.emailAddress = emailAddressError;
      }

      const phoneNumberError = this.validatePhoneNumber(
        contact.contactPhoneNumber,
        "Phone Number"
      );
      if (phoneNumberError) {
        errors.contactPhoneNumber = phoneNumberError;
      }

      const cellNumberError = this.validatePhoneNumber(
        contact.contactCellNumber,
        "Cell Number"
      );
      if (cellNumberError) {
        errors.contactCellNumber = cellNumberError;
      }

      this.validationErrors = errors;
      return Object.keys(errors).length === 0;
    },
    saveContact() {
      if (!this.hasContactValues(this.modalContact)) {
        this.showWarningAlert("Capture at least one contact field before saving.");
        return;
      }

      if (!this.validateContact(this.modalContact)) {
        const firstValidationError = Object.values(this.validationErrors)[0];
        this.showWarningAlert(firstValidationError);
        return;
      }

      this.modalSaving = true;

      const contact = Object.assign({}, this.modalContact, {
        supplierNumber: this.supplierNumber,
      });

      if (contact.supplierContactNumber > 0) {
        this.updateContact(contact);
        return;
      }

      this.createContact(contact);
    },
    createContact(contact) {
      this.$http
        .post(
          this.$config.aimsAPIv3 +
            "suppliers/" +
            this.supplierNumber +
            "/create/contact",
          contact
        )
        .then(
          () => {
            this.finishContactSave("Supplier contact saved");
          },
          (error) => {
            this.modalSaving = false;
            console.error(error);
            this.showError("Error Creating Contact", error);
          }
        );
    },
    updateContact(contact) {
      this.$http.put(this.$config.aimsAPIv3 + "suppliers/update/contact", contact).then(
        () => {
          this.finishContactSave("Supplier contact updated");
        },
        (error) => {
          this.modalSaving = false;
          console.error(error);
          this.showError("Error Updating Contact", error);
        }
      );
    },
    finishContactSave(successMessage) {
      this.modalSaving = false;
      this.$refs.contactModal.close();
      this.showSuccess(successMessage);
      this.resetModalState();
      this.loadPage();
    },
    clearAssignedRoles(supplier, contactNumber) {
      let supplierChanged = false;

      for (let i = 0; i < this.roleConfigs.length; i++) {
        const role = this.roleConfigs[i];
        if (supplier[role.supplierField] === contactNumber) {
          supplier[role.supplierField] = 0;
          supplierChanged = true;
        }
      }

      return supplierChanged;
    },
    deleteContact(contact) {
      const updatedSupplier = Object.assign({}, this.supplier);
      const supplierChanged = this.clearAssignedRoles(
        updatedSupplier,
        contact.supplierContactNumber
      );

      if (supplierChanged) {
        this.$http.put(this.$config.aimsAPIv3 + "suppliers/update", updatedSupplier).then(
          () => {
            this.deleteContactRecord(contact.supplierContactNumber);
          },
          (error) => {
            console.error(error);
            this.showError("Error Updating Supplier", error);
          }
        );
        return;
      }

      this.deleteContactRecord(contact.supplierContactNumber);
    },
    deleteContactRecord(contactNumber) {
      this.$http.delete(this.$config.aimsAPIv3 + "suppliers/delete/contact/" + contactNumber).then(
        () => {
          this.showSuccess("Supplier contact deleted");
          this.loadPage();
        },
        (error) => {
          console.error(error);
          this.showError("Error Deleting Contact", error);
        }
      );
    },
    saveAssignments() {
      this.assignmentSaving = true;

      this.$http.put(this.$config.aimsAPIv3 + "suppliers/update", this.supplier).then(
        () => {
          this.assignmentSaving = false;
          this.showSuccess("Supplier role assignments updated");
          this.loadPage();
        },
        (error) => {
          this.assignmentSaving = false;
          console.error(error);
          this.showError("Error Updating Supplier", error);
        }
      );
    },
  },
};
</script>
